/**
 * Report Data Transformation Utilities
 *
 * Converts raw BigQuery data from backend into the format expected by
 * chart and table components
 */

// Table types
import type { Cuadro04Data } from '../components/report/tables/Cuadro04';
import type { InvestmentData } from '../components/report/tables/Cuadro05';
import type { HistoricalIndicatorData } from '../components/report/tables/Anexo1';

// NEW chart/table types (matching PDF report format)
import type { Grafico04Data } from '../components/report/charts/Grafico04';
import type { Grafico05Data } from '../components/report/charts/Grafico05';
import type { Grafico06Data } from '../components/report/charts/Grafico06';
import type { Grafico07V2Data } from '../components/report/charts/Grafico07_v2';
import type { Grafico08V2Data } from '../components/report/charts/Grafico08_v2';
import type { Grafico09V2Data } from '../components/report/charts/Grafico09_v2';
import type { Grafico10V2Data } from '../components/report/charts/Grafico10_v2';
import type { Cuadro06Data } from '../components/report/tables/Cuadro06';

// ============================================================================
// Type Definitions for Backend Data
// ============================================================================

export interface BackendIndicatorRow {
  indicator_id: string;
  indicator_name: string;
  indicator_value: number;
  indicator_score: number;
  indicator_category?: string;
  calculation_period: string;
}

export interface BackendBalanceRow {
  account_code: string;
  account_name: string;
  account_value: number;
  calculation_period: string;
}

export interface BackendReportData {
  metadata: {
    institution_id: string;
    period: string;
    generation_date: string;
  };
  data: {
    currentIndicators: BackendIndicatorRow[];
    currentBalance: BackendBalanceRow[];
    historicalIndicators: BackendIndicatorRow[];
    historicalBalance: BackendBalanceRow[];
  };
  narratives: {
    solvencia?: string;
    liquidez?: string;
    calidad_activos?: string;
    eficiencia_gestion?: string;
    rentabilidad?: string;
    riesgo_interes?: string;
  };
}

// ============================================================================
// Balance Sheet Account Code Mappings
// ============================================================================

/**
 * Balance sheet account codes for Ecuadorian cooperatives (SEPS structure)
 *
 * Account Hierarchy:
 * - 1-digit codes (1, 2, 3): Main categories (totals already aggregated in BigQuery)
 * - 2-digit codes (11, 12, 13): Subcategories (components of main categories)
 *
 * Source: composed-night-434902-u0.bankinglatam.balance_sheet_items
 *
 * IMPORTANT: Do NOT sum main categories with their subcategories!
 * Example: cuenta_cop_1 (ACTIVO) already includes cuenta_cop_11, 12, 13, 14, etc.
 */
const BALANCE_ACCOUNTS = {
  // Main Categories (1-digit codes) - These are TOTALS
  ACTIVO: 'cuenta_cop_1',              // Code 1: ACTIVO (Total Assets)
  PASIVO: 'cuenta_cop_2',              // Code 2: PASIVOS (Total Liabilities)
  PATRIMONIO: 'cuenta_cop_3',          // Code 3: PATRIMONIO (Total Equity)

  // Asset Subcategories (2-digit codes starting with 1)
  FONDOS_DISPONIBLES: 'cuenta_cop_11', // Code 11: FONDOS DISPONIBLES (Available Funds)
  INVERSIONES: 'cuenta_cop_13',        // Code 13: INVERSIONES (Investments)
  CARTERA_CREDITO: 'cuenta_cop_14',    // Code 14: CARTERA DE CRÉDITOS (Loan Portfolio)

  // Liability Subcategories (2-digit codes starting with 2)
  DEPOSITOS: 'cuenta_cop_21',          // Code 21: OBLIGACIONES CON EL PÚBLICO (Deposits)
};

// ============================================================================
// Table Data Transformers
// ============================================================================

/**
 * Transform balance sheet data for Cuadro 04 (Balance Evolution)
 */
export function transformCuadro04Data(
  historicalBalance: BackendBalanceRow[]
): Cuadro04Data[] {
  console.log('🔍 Cuadro04 Transform - Input rows:', historicalBalance.length);
  if (historicalBalance.length > 0) {
    console.log('🔍 Sample data:', historicalBalance.slice(0, 3));
  }

  // Group by period
  const periodGroups = new Map<string, BackendBalanceRow[]>();
  historicalBalance.forEach(row => {
    if (!periodGroups.has(row.calculation_period)) {
      periodGroups.set(row.calculation_period, []);
    }
    periodGroups.get(row.calculation_period)!.push(row);
  });

  console.log('🔍 Periods found:', Array.from(periodGroups.keys()));

  // Transform each period
  const result: Cuadro04Data[] = [];
  periodGroups.forEach((rows, period) => {
    // Get main category totals (these are already aggregated in BigQuery)
    // Do NOT use startsWith() as that would double-count by including sub-accounts
    const activoRow = rows.find(r => r.account_code === BALANCE_ACCOUNTS.ACTIVO);
    const pasivoRow = rows.find(r => r.account_code === BALANCE_ACCOUNTS.PASIVO);
    const patrimonioRow = rows.find(r => r.account_code === BALANCE_ACCOUNTS.PATRIMONIO);

    console.log(`🔍 Period ${period}:`);
    console.log(`   Activo found: ${!!activoRow}, Pasivo found: ${!!pasivoRow}, Patrimonio found: ${!!patrimonioRow}`);

    // Convert to numbers explicitly in case they come as strings from BigQuery
    const activo = activoRow ? parseFloat(String(activoRow.account_value)) : 0;
    const pasivo = pasivoRow ? parseFloat(String(pasivoRow.account_value)) : 0;
    const patrimonio = patrimonioRow ? parseFloat(String(patrimonioRow.account_value)) : 0;

    console.log(`   Totals: Activo=${activo}, Pasivo=${pasivo}, Patrimonio=${patrimonio}`);

    result.push({
      period,
      activo,
      pasivo,
      patrimonio,
    });
  });

  console.log('🔍 Cuadro04 final result:', result.length, 'periods');
  return result.sort((a, b) => b.period.localeCompare(a.period));
}

/**
 * Transform balance sheet data for Cuadro 05 (Investment Portfolio)
 */
export function transformCuadro05Data(
  historicalBalance: BackendBalanceRow[]
): InvestmentData[] {
  console.log('🔍 Cuadro05 Transform - Input rows:', historicalBalance.length);
  if (historicalBalance.length > 0) {
    console.log('🔍 Sample data:', historicalBalance.slice(0, 3));
  }

  // Group by period
  const periodGroups = new Map<string, BackendBalanceRow[]>();
  historicalBalance.forEach(row => {
    if (!periodGroups.has(row.calculation_period)) {
      periodGroups.set(row.calculation_period, []);
    }
    periodGroups.get(row.calculation_period)!.push(row);
  });

  console.log('🔍 Periods found:', Array.from(periodGroups.keys()));

  // Transform each period
  const result: InvestmentData[] = [];
  periodGroups.forEach((rows, period) => {
    console.log(`🔍 Period ${period}:`, rows.length, 'rows');
    console.log('🔍 Available account_codes:', rows.map(r => r.account_code));

    // Get specific account values (these are already aggregated in BigQuery)
    // Convert to numbers explicitly in case they come as strings
    const fondos_disponibles = parseFloat(String(rows.find(r => r.account_code === BALANCE_ACCOUNTS.FONDOS_DISPONIBLES)?.account_value || 0));
    const inversiones = parseFloat(String(rows.find(r => r.account_code === BALANCE_ACCOUNTS.INVERSIONES)?.account_value || 0));
    const total_activos_liquidos = fondos_disponibles + inversiones;
    const activo_total = parseFloat(String(rows.find(r => r.account_code === BALANCE_ACCOUNTS.ACTIVO)?.account_value || 0));

    console.log(`🔍   Fondos: ${fondos_disponibles} (${typeof fondos_disponibles})`);
    console.log(`🔍   Inversiones: ${inversiones} (${typeof inversiones})`);
    console.log(`🔍   Total Líquidos: ${total_activos_liquidos} (${typeof total_activos_liquidos})`);
    console.log(`🔍   Activo Total: ${activo_total} (${typeof activo_total})`);

    const dataPoint = {
      period,
      fondos_disponibles,
      inversiones,
      total_activos_liquidos,
      activo_total,
    };

    console.log('🔍   Pushing data point:', JSON.stringify(dataPoint));
    result.push(dataPoint);
  });

  console.log('🔍 Cuadro05 final result:', result.length, 'periods');
  if (result.length > 0) {
    console.log('🔍 First result:', JSON.stringify(result[0]));
  }
  return result.sort((a, b) => b.period.localeCompare(a.period));
}

/**
 * Transform indicator data for Anexo 1 (Historical Indicators Table)
 */
export function transformAnexo1Data(
  historicalIndicators: BackendIndicatorRow[]
): HistoricalIndicatorData[] {
  // Map indicator IDs to components
  const indicatorComponentMap: Record<string, string> = {
    // Capital (C)
    C1: 'C', C2: 'C', C3: 'C', C4: 'C',
    // Solvency (S)
    S1: 'S', S2: 'S', S3: 'S', S4: 'S', S5: 'S', S6: 'S', S7: 'S',
    // Liquidity (L)
    L1: 'L', L2: 'L', L3: 'L', L4: 'L', L5: 'L',
    // Efficiency (EG)
    EG1: 'EG', EG2: 'EG', EG3: 'EG', EG4: 'EG',
    // Profitability (R)
    R1: 'R', R2: 'R', R3: 'R', R4: 'R',
    // Interest Rate Risk (STI)
    STI1: 'STI', STI2: 'STI', STI3: 'STI',
  };

  return historicalIndicators.map(row => ({
    period: row.calculation_period,
    component: indicatorComponentMap[row.indicator_id] || 'OTHER',
    indicator_id: row.indicator_id,
    indicator_name: row.indicator_name,
    indicator_value: row.indicator_value,
    indicator_score: row.indicator_score,
  }));
}

// ============================================================================
// Chart Period Filtering - Matching PDF Report Format
// ============================================================================

/**
 * Calculate which periods to display for charts based on the report period.
 * Returns: Historical December periods + quarterly periods for recent data + current period
 *
 * Example for 2025-05:
 * - Historical Dec: 2020-12, 2021-12, 2022-12, 2023-12
 * - Recent quarters: 2024-03, 2024-06, 2024-09, 2024-12, 2025-03
 * - Current: 2025-05
 */
export function getChartDisplayPeriods(
  allPeriods: string[],
  yearsBack: number = 5
): string[] {
  if (allPeriods.length === 0) return [];

  // Sort periods chronologically
  const sortedPeriods = [...allPeriods].sort();
  const currentPeriod = sortedPeriods[sortedPeriods.length - 1];
  const [currentYear, currentMonth] = currentPeriod.split('-').map(Number);

  // Calculate cutoff year for quarterly data (2 years back from current)
  const quarterlyStartYear = currentYear - 2;

  const displayPeriods: Set<string> = new Set();

  for (const period of sortedPeriods) {
    const [year, month] = period.split('-').map(Number);

    // Always include December periods for historical years (up to yearsBack years)
    if (month === 12 && year >= currentYear - yearsBack) {
      displayPeriods.add(period);
    }

    // Include quarterly periods (03, 06, 09, 12) for recent years
    if (year >= quarterlyStartYear && [3, 6, 9, 12].includes(month)) {
      displayPeriods.add(period);
    }

    // Always include the current period
    if (period === currentPeriod) {
      displayPeriods.add(period);
    }
  }

  // Sort and return
  return Array.from(displayPeriods).sort();
}

/**
 * Filter data array to only include chart display periods
 */
export function filterToChartPeriods<T extends { period: string }>(
  data: T[],
  yearsBack: number = 5
): T[] {
  const allPeriods = data.map(d => d.period);
  const displayPeriods = new Set(getChartDisplayPeriods(allPeriods, yearsBack));
  return data.filter(d => displayPeriods.has(d.period));
}

// ============================================================================
// Chart Data Transformers - Matching PDF Report Format (04-10)
// ============================================================================

/**
 * Transform for Gráfico 04 (NEW): Ingresos/Activo Productivo
 * Uses R3 indicator for income/productive assets ratio
 */
export function transformGrafico04DataNew(
  historicalIndicators: BackendIndicatorRow[]
): Grafico04Data[] {
  const allData = historicalIndicators
    .filter(row => row.indicator_id === 'R3')
    .map(row => ({
      period: row.calculation_period,
      ingreso_activo_productivo: row.indicator_value,
      // benchmark_s1 would come from segment data if available
    }));

  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(allData);
}

/**
 * Transform for Gráfico 05 (NEW): Intereses Causados/Activo Productivo
 * Uses EG1 indicator for cost/productive assets ratio
 */
export function transformGrafico05DataNew(
  historicalIndicators: BackendIndicatorRow[]
): Grafico05Data[] {
  const allData = historicalIndicators
    .filter(row => row.indicator_id === 'EG1')
    .map(row => ({
      period: row.calculation_period,
      costo_activo_productivo: row.indicator_value,
      // benchmark_s1 would come from segment data if available
    }));

  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(allData);
}

/**
 * Transform for Gráfico 06 (NEW): Composición del Patrimonio
 * Uses balance sheet equity subcategories
 */
export function transformGrafico06DataNew(
  historicalBalance: BackendBalanceRow[]
): Grafico06Data[] {
  // Group by period
  const periodGroups = new Map<string, BackendBalanceRow[]>();
  historicalBalance.forEach(row => {
    if (!periodGroups.has(row.calculation_period)) {
      periodGroups.set(row.calculation_period, []);
    }
    periodGroups.get(row.calculation_period)!.push(row);
  });

  const result: Grafico06Data[] = [];
  periodGroups.forEach((rows, period) => {
    // Get equity subcategory values
    const capital_social = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_31')?.account_value || 0));
    const reservas = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_33')?.account_value || 0));
    const superavit = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_34')?.account_value || 0));
    const resultados = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_36')?.account_value || 0));

    // Calculate total patrimonio and activo for leverage
    const patrimonio = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_3')?.account_value || 0));
    const activo = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_1')?.account_value || 0));

    // Leverage = Activo / Patrimonio
    const apalancamiento = patrimonio > 0 ? activo / patrimonio : 0;

    result.push({
      period,
      capital_social,
      reservas,
      superavit_valuaciones: superavit,
      resultados,
      apalancamiento,
    });
  });

  const sortedResult = result.sort((a, b) => a.period.localeCompare(b.period));
  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(sortedResult);
}

/**
 * Transform for Gráfico 07 (NEW): Solvencia
 * Uses S1 indicator for capital adequacy ratio
 */
export function transformGrafico07DataNew(
  historicalIndicators: BackendIndicatorRow[],
  historicalBalance: BackendBalanceRow[]
): Grafico07V2Data[] {
  // Get S1 (Ratio de Capital Global) by period
  const s1Map = new Map<string, number>();
  historicalIndicators
    .filter(row => row.indicator_id === 'S1')
    .forEach(row => {
      s1Map.set(row.calculation_period, row.indicator_value);
    });

  // Get total activo by period
  const activoMap = new Map<string, number>();
  historicalBalance
    .filter(row => row.account_code === 'cuenta_cop_1')
    .forEach(row => {
      activoMap.set(row.calculation_period, parseFloat(String(row.account_value)));
    });

  // Combine into result
  const periods = new Set([...s1Map.keys(), ...activoMap.keys()]);
  const result: Grafico07V2Data[] = [];

  periods.forEach(period => {
    const activo = activoMap.get(period);
    const ratio = s1Map.get(period);
    if (activo !== undefined && ratio !== undefined) {
      result.push({
        period,
        activo,
        ratio_capital_global: ratio,
      });
    }
  });

  const sortedResult = result.sort((a, b) => a.period.localeCompare(b.period));
  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(sortedResult);
}

/**
 * Transform for Gráfico 08 (NEW): Liquidez General
 * Uses L3 indicator (Fondos disponibles / obligaciones a la vista)
 * Note: L4 (Liquidez General) is defined but not calculated in the database
 */
export function transformGrafico08DataNew(
  historicalIndicators: BackendIndicatorRow[]
): Grafico08V2Data[] {
  const allData = historicalIndicators
    .filter(row => row.indicator_id === 'L4')
    .map(row => ({
      period: row.calculation_period,
      liquidez_coac: row.indicator_value,
      // liquidez_s1 would come from segment data if available
    }));

  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(allData);
}

/**
 * Transform for Gráfico 09 (NEW): Riesgo de Crédito
 * Uses C1 (coverage) and C2 (delinquency) indicators
 */
export function transformGrafico09DataNew(
  historicalIndicators: BackendIndicatorRow[]
): Grafico09V2Data[] {
  // Group by period
  const periodMap = new Map<string, { coverage?: number; mora?: number }>();

  historicalIndicators.forEach(row => {
    // C4 = Cobertura Provisiones (Provisiones / Cartera Improductiva)
    // C2 = Morosidad (Cartera Improductiva / Cartera Bruta)
    if (row.indicator_id === 'C4' || row.indicator_id === 'C2') {
      if (!periodMap.has(row.calculation_period)) {
        periodMap.set(row.calculation_period, {});
      }
      const entry = periodMap.get(row.calculation_period)!;
      if (row.indicator_id === 'C4') {
        entry.coverage = row.indicator_value;
      } else if (row.indicator_id === 'C2') {
        entry.mora = row.indicator_value;
      }
    }
  });

  const result: Grafico09V2Data[] = [];
  periodMap.forEach((values, period) => {
    if (values.coverage !== undefined && values.mora !== undefined) {
      result.push({
        period,
        cobertura_provisiones: values.coverage,
        mora: values.mora,
      });
    }
  });

  const sortedResult = result.sort((a, b) => a.period.localeCompare(b.period));
  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(sortedResult);
}

/**
 * Transform for Gráfico 10 (NEW): Eficiencia y Gestión
 * Uses EG indicators for efficiency breakdown
 */
export function transformGrafico10DataNew(
  historicalIndicators: BackendIndicatorRow[]
): Grafico10V2Data[] {
  // Group by period
  const periodMap = new Map<string, { eg1?: number; eg2?: number; eg4?: number; r3?: number }>();

  historicalIndicators.forEach(row => {
    if (['EG1', 'EG2', 'EG4', 'R3'].includes(row.indicator_id)) {
      if (!periodMap.has(row.calculation_period)) {
        periodMap.set(row.calculation_period, {});
      }
      const entry = periodMap.get(row.calculation_period)!;
      if (row.indicator_id === 'EG1') entry.eg1 = row.indicator_value;
      else if (row.indicator_id === 'EG2') entry.eg2 = row.indicator_value;
      else if (row.indicator_id === 'EG4') entry.eg4 = row.indicator_value;
      else if (row.indicator_id === 'R3') entry.r3 = row.indicator_value;
    }
  });

  const result: Grafico10V2Data[] = [];
  periodMap.forEach((values, period) => {
    // Calculate percentages relative to income
    // EG1 = Costo financiero / Activo productivo
    // EG2 = Gasto operativo / Activo productivo
    // EG4 = Provisiones / Activo productivo
    // Margen = R3 - EG1 - EG2 - EG4 (roughly)
    const costo = values.eg1 || 0;
    const gasto = values.eg2 || 0;
    const provision = values.eg4 || 0;
    const ingreso = values.r3 || 0;

    // Normalize as percentages of income
    const total = ingreso > 0 ? ingreso : 1;

    result.push({
      period,
      costo_pct: costo / total,
      gasto_pct: gasto / total,
      provision_pct: provision / total,
      margen_intermediacion: (ingreso - costo - gasto - provision) / total,
    });
  });

  const sortedResult = result.sort((a, b) => a.period.localeCompare(b.period));
  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(sortedResult);
}

/**
 * Transform for Cuadro 06 (NEW): Evolución Balance
 * Same as Cuadro04 but with different ID
 */
export function transformCuadro06Data(
  historicalBalance: BackendBalanceRow[]
): Cuadro06Data[] {
  // Same logic as transformCuadro04Data
  const periodGroups = new Map<string, BackendBalanceRow[]>();
  historicalBalance.forEach(row => {
    if (!periodGroups.has(row.calculation_period)) {
      periodGroups.set(row.calculation_period, []);
    }
    periodGroups.get(row.calculation_period)!.push(row);
  });

  const result: Cuadro06Data[] = [];
  periodGroups.forEach((rows, period) => {
    const activo = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_1')?.account_value || 0));
    const pasivo = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_2')?.account_value || 0));
    const patrimonio = parseFloat(String(rows.find(r => r.account_code === 'cuenta_cop_3')?.account_value || 0));

    result.push({ period, activo, pasivo, patrimonio });
  });

  const sortedResult = result.sort((a, b) => a.period.localeCompare(b.period));
  // Filter to chart display periods (yearly + quarterly for recent)
  return filterToChartPeriods(sortedResult);
}

// ============================================================================
// Main Transform Function
// ============================================================================

/**
 * Transform complete backend report data into all component formats
 */
export function transformReportData(backendData: BackendReportData) {
  const { data } = backendData;

  return {
    // Tables
    cuadro04: transformCuadro04Data(data.historicalBalance),
    cuadro05: transformCuadro05Data(data.historicalBalance),
    cuadro06: transformCuadro06Data(data.historicalBalance),
    anexo1: transformAnexo1Data(data.historicalIndicators),

    // Charts matching PDF format (04-10)
    grafico04_new: transformGrafico04DataNew(data.historicalIndicators),
    grafico05_new: transformGrafico05DataNew(data.historicalIndicators),
    grafico06_new: transformGrafico06DataNew(data.historicalBalance),
    grafico07_new: transformGrafico07DataNew(data.historicalIndicators, data.historicalBalance),
    grafico08_new: transformGrafico08DataNew(data.historicalIndicators),
    grafico09_new: transformGrafico09DataNew(data.historicalIndicators),
    grafico10_new: transformGrafico10DataNew(data.historicalIndicators),

    // Narratives (pass through)
    narratives: backendData.narratives,
  };
}
